import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton } from '@mui/material';
import numWords from 'num-words';
import { FC, useState } from 'react';
import NoImage from '../../../../../assets/no-image.jpg';
import { env } from '../../../../../config/env';
import { VACANCY_BASE_PATH } from '../../../../../services/endpoints/rec/vacancy.endpoints';
import type { VacancyApiResponse } from '../../../../../services/types/vacancy.types';
import {
  buildBudgetText,
  calculateDaysRemaining,
  formatVacancyDateTime,
} from '../../../../../utils/vacancyCalc';
import ViewVacancySkillOrTechnologyCard from './ViewVacancySkillOrTechnologyCard';
import ViewVacancyInterviewQuestions from './ViewVacancyInterviewQuestions';

interface ViewVacancyContentProps {
  vacancyData: VacancyApiResponse | null;
}

const ViewVacancyContent: FC<ViewVacancyContentProps> = ({ vacancyData }) => {
  const [showJD, setShowJD] = useState(false);

  if (!vacancyData) return null;

  const totalOpportunities = vacancyData?.customers.reduce((sum, c) => sum + c.numberOfVacancies, 0) || 0;

  const designation = vacancyData?.designation?.designation || '';
  const designationPlural = totalOpportunities > 1 ? designation + 's' : designation;

  const daysRemaining = calculateDaysRemaining(vacancyData?.vacancyDueDate);

  const locationsWithBudgets = vacancyData?.locations?.filter(loc => loc.country !== null) || [];

  const budgetText = buildBudgetText(locationsWithBudgets);
  const daysText = daysRemaining > 0 ? `Resources are required within <b>${daysRemaining} days</b>` : '';

  return (
    <>
      {/* Vacancy Details Summary Section */}
      <Box className="view-vacancy-section" sx={{ mb: 2, background: '#FAFAFF', boxShadow: 'none', border: 'none' }}>
        <div 
          style={{ fontSize: 19, marginBottom: 16, fontWeight: 500 }}
          dangerouslySetInnerHTML={{
            __html: `<b>${numWords(totalOpportunities).replace(/\b\w/g, char => char.toUpperCase())}</b> ${totalOpportunities === 1 ? 'Opportunity' : 'Opportunities'} for <b>${designationPlural}</b> with at least <b>${vacancyData?.minimumIndustryExperience} ${vacancyData?.minimumIndustryExperience > 1 ? ' Years' : ' Year'}</b> of experience, as <b>${vacancyData?.vacancyReason}</b>${daysRemaining > 0 && budgetText ? `. ${daysText}, ${budgetText}` : ''}${daysRemaining > 0 && !budgetText ? `. ${daysText}` : ''}${daysRemaining <= 0 && budgetText ? `. ${budgetText}` : ''}.`
          }}
        />
        <Box display="flex" gap={2}>
          {vacancyData?.customers.map((customer) => (
            <Box key={customer.id} className="customer-card">
              {/* Building Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.58 11.3C15.0345 10.7319 15.2846 10.0275 15.29 9.3C15.29 8.42744 14.9434 7.59061 14.3264 6.97362C13.7094 6.35662 12.8726 6.01 12 6.01C11.1274 6.01 10.2906 6.35662 9.67362 6.97362C9.05662 7.59061 8.71 8.42744 8.71 9.3C8.71542 10.0275 8.96551 10.7319 9.42 11.3C8.52863 11.8424 7.8293 12.6502 7.42 13.61C7.36271 13.7318 7.3306 13.8639 7.32558 13.9984C7.32056 14.1329 7.34274 14.267 7.39079 14.3927C7.43884 14.5184 7.51177 14.6331 7.60521 14.73C7.69865 14.8268 7.81068 14.9038 7.93459 14.9563C8.0585 15.0089 8.19174 15.0358 8.32632 15.0357C8.4609 15.0355 8.59406 15.0081 8.71782 14.9552C8.84157 14.9023 8.95338 14.825 9.04655 14.7279C9.13971 14.6308 9.21231 14.5159 9.26 14.39C9.48983 13.8527 9.87157 13.3943 10.3583 13.071C10.8451 12.7477 11.4157 12.5735 12 12.57C12.586 12.5716 13.1588 12.7448 13.6475 13.0683C14.1362 13.3917 14.5195 13.8512 14.75 14.39C14.8266 14.5707 14.9546 14.725 15.1182 14.8334C15.2818 14.9419 15.4737 14.9998 15.67 15C15.8038 14.9975 15.936 14.9704 16.06 14.92C16.3039 14.8164 16.4966 14.6203 16.596 14.3747C16.6954 14.129 16.6932 13.854 16.59 13.61C16.1781 12.6487 15.4751 11.8408 14.58 11.3ZM12 10.57C11.7444 10.57 11.4946 10.4941 11.2823 10.3519C11.0699 10.2097 10.9046 10.0076 10.8073 9.77132C10.7099 9.53502 10.685 9.27514 10.7356 9.02463C10.7862 8.77413 10.91 8.5443 11.0914 8.36429C11.2728 8.18429 11.5036 8.06222 11.7545 8.01358C12.0054 7.96494 12.2651 7.99192 12.5006 8.09109C12.7362 8.19026 12.9369 8.35716 13.0775 8.57061C13.218 8.78406 13.292 9.03445 13.29 9.29C13.2874 9.63039 13.1503 9.95595 12.9087 10.1957C12.667 10.4355 12.3404 10.57 12 10.57ZM18 2H6C5.20435 2 4.44129 2.31607 3.87868 2.87868C3.31607 3.44129 3 4.20435 3 5V16C3 16.7956 3.31607 17.5587 3.87868 18.1213C4.44129 18.6839 5.20435 19 6 19H8.59L11.29 21.71C11.3834 21.8027 11.4943 21.876 11.6161 21.9258C11.7379 21.9755 11.8684 22.0008 12 22C12.2383 22 12.4689 21.9149 12.65 21.76L15.87 19H18C18.7956 19 19.5587 18.6839 20.1213 18.1213C20.6839 17.5587 21 16.7956 21 16V5C21 4.20435 20.6839 3.44129 20.1213 2.87868C19.5587 2.31607 18.7956 2 18 2ZM19 16C19 16.2652 18.8946 16.5196 18.7071 16.7071C18.5196 16.8946 18.2652 17 18 17H15.5C15.2617 17 15.0311 17.0851 14.85 17.24L12.05 19.64L9.71 17.29C9.61656 17.1973 9.50574 17.124 9.38391 17.0742C9.26207 17.0245 9.13161 16.9992 9 17H6C5.73478 17 5.48043 16.8946 5.29289 16.7071C5.10536 16.5196 5 16.2652 5 16V5C5 4.73478 5.10536 4.48043 5.29289 4.29289C5.48043 4.10536 5.73478 4 6 4H18C18.2652 4 18.5196 4.10536 18.7071 4.29289C18.8946 4.48043 19 4.73478 19 5V16Z" fill="#702DFF" />
              </svg>
              <span style={{ fontWeight: 500, fontSize: 15, marginLeft: 4, marginBottom: 2.5 }}>
                {customer.name}, {customer.countryCode}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontWeight: 500, fontSize: 15, marginLeft: 12, marginBottom: 2.5 }}>
                {customer.numberOfVacancies.toString().padStart(2, '0')}
              </span>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Skills */}
      <ViewVacancySkillOrTechnologyCard
        title="Skills"
        primary={vacancyData?.primarySkills}
        secondary={vacancyData?.secondarySkills}
      />

      {/* Technologies */}
      <ViewVacancySkillOrTechnologyCard
        title="Technologies"
        primary={vacancyData?.primaryTechnologies}
        secondary={vacancyData?.secondaryTechnologies}
      />

      {/* Qualification */}
      <Box className="view-vacancy-section" sx={{ mb: 2 }}>
        <div className="qualification-title">Qualifications</div>
        {vacancyData?.qualifications.length > 0 && <Box className="view-row-box">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {vacancyData?.qualifications.map((qualification) => (
              <Box className="view-column-box">
                <div className="basic-card-title">{qualification.qualificationType}</div>
                <div className="basic-card-value">{qualification.name}</div>
              </Box>
            ))}
          </Box>
        </Box>}
      </Box>

      {/* Job Description Collapsible Card */}
      <Box className="view-vacancy-section" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
          <div className="job-description-title">Job Description</div>
          <IconButton onClick={() => setShowJD((prev) => !prev)} className="jd-expand-collapse-button">
            {showJD ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={showJD} timeout="auto" unmountOnExit>
          <Box mb={2}>
            <div className="job-description-body" dangerouslySetInnerHTML={{ __html: vacancyData?.jobDescription || '' }} />
          </Box>
        </Collapse>
      </Box>

      {/* Vacancy URL */}
      <Box className="view-vacancy-section" sx={{ mb: 2 }} display="flex" gap={2} alignItems="center">
        {/* Main URL */}
        <Box display="flex" alignItems="center" gap={1} flex={2}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1415_14567)">
              <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M1.5 9H16.5" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M9 1.5C10.876 3.55376 11.9421 6.21903 12 9C11.9421 11.781 10.876 14.4462 9 16.5C7.12404 14.4462 6.05794 11.781 6 9C6.05794 6.21903 7.12404 3.55376 9 1.5V1.5Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </g>
            <defs>
              <clipPath id="clip0_1415_14567">
                <rect width="18" height="18" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <a href={vacancyData?.vacancyUrl} target="_blank" rel="noopener noreferrer" className="url-value">
            {vacancyData?.vacancyUrl ? vacancyData?.vacancyUrl : 'N/A'}
          </a>
        </Box>
        {/* Tiny URL */}
        <Box display="flex" alignItems="center" gap={1} flex={2}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1429_16380)">
              <path d="M13.4598 9.73867L16.7569 6.44143C18.1063 5.0922 18.1063 2.89691 16.7568 1.54768L16.2212 1.01208C14.8719 -0.33733 12.6767 -0.33733 11.3274 1.01191L8.03017 4.30902C7.28608 5.0531 6.95317 6.05475 7.02979 7.02979C6.05484 6.95309 5.05315 7.286 4.30914 8.03021L1.01199 11.3274C-0.33733 12.6765 -0.33733 14.8718 1.01199 16.2212L1.54776 16.7568C2.89704 18.1061 5.09232 18.1061 6.44152 16.7568L9.73867 13.4597C10.4829 12.7155 10.8158 11.7138 10.7391 10.739C11.714 10.8158 12.7156 10.4828 13.4598 9.73867ZM8.57355 12.2944L5.27635 15.5917C4.56963 16.2983 3.41957 16.2983 2.71301 15.5917L2.17715 15.0558C1.47042 14.3493 1.47051 13.1992 2.17715 12.4923L5.47435 9.19529C6.07256 8.59703 6.98803 8.50544 7.68385 8.91962L6.31726 10.2864C5.99548 10.6081 5.99548 11.1299 6.31726 11.4516C6.639 11.7734 7.16073 11.7734 7.48243 11.4516L8.84918 10.0849C9.2634 10.7808 9.17185 11.6964 8.57355 12.2944ZM10.0851 8.84914L11.4516 7.48238C11.7734 7.16077 11.7734 6.639 11.4516 6.31735C11.1299 5.9954 10.6082 5.9954 10.2864 6.31718L8.91979 7.6838C8.50556 6.98803 8.59712 6.07248 9.19537 5.47431L12.4925 2.17715C13.1993 1.47034 14.3494 1.47034 15.056 2.17715L15.5918 2.71292C16.2985 3.41965 16.2985 4.56958 15.5918 5.27631L12.2946 8.57355C11.6964 9.17185 10.7809 9.26332 10.0851 8.84914Z" fill="black" />
            </g>
            <defs>
              <clipPath id="clip0_1429_16380">
                <rect width="18" height="18" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <a href={vacancyData?.vacancyTinyUrl} target="_blank" rel="noopener noreferrer" className="url-value">
            {vacancyData?.vacancyTinyUrl ? vacancyData?.vacancyTinyUrl : 'N/A'}
          </a>
        </Box>
        <Box flex={1} />
      </Box>

      {/* Social Media Section */}
      <Box className="view-vacancy-section" sx={{ mb: 2 }}>
        <div className="social-media-title">Social Media</div>
        {vacancyData?.vacancyTinyUrl && (
          <div className="social-media-url">{vacancyData?.vacancyTinyUrl}</div>
        )}
        <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
          <Box display="flex" gap={2} alignItems="start">
            {/* LinkedIn */}
            <Box>
              <div className="social-media-image-title">LinkedIn</div>
              <div className="social-media-row">
                <img
                  alt="LinkedIn"
                  className="social-media-image"
                  src={vacancyData?.imageLinkedIn ? `${env.apiBaseUrl}${VACANCY_BASE_PATH}${vacancyData.imageLinkedIn}` : NoImage}
                />
              </div>
            </Box>
            {/* Facebook */}
            <Box>
              <div className="social-media-image-title">Facebook</div>
              <div className="social-media-row">
                <img
                  alt="Facebook"
                  className="social-media-image"
                  src={vacancyData?.imageFB ? `${env.apiBaseUrl}${VACANCY_BASE_PATH}${vacancyData.imageFB}` : NoImage}
                />
              </div>
            </Box>
            {/* Instagram */}
            <Box>
              <div className="social-media-image-title">Instagram</div>
              <div className="social-media-row">
                <img
                  alt="Instagram"
                  className="social-media-image"
                  src={vacancyData?.imageInstagram ? `${env.apiBaseUrl}${VACANCY_BASE_PATH}${vacancyData.imageInstagram}` : NoImage}
                />
              </div>
            </Box>
            <Box ml={4} display="flex" flexDirection="column" gap={3}>
              <Box>
                <div className="social-media-tags-desc-title">Tags</div>
                <div className="social-media-tags-desc-value">{vacancyData?.socialMediaTags ? vacancyData?.socialMediaTags : 'N/A'}</div>
              </Box>
              <Box>
                <div className="social-media-tags-desc-title">Description</div>
                <div className="social-media-tags-desc-value">{vacancyData?.socialMediaDesc ? vacancyData?.socialMediaDesc : 'N/A'}</div>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <ViewVacancyInterviewQuestions vacancyId={vacancyData.id} />

      {/* Audit Details Section */}
      <Box className="view-vacancy-section" sx={{ mb: 2 }}>
        <div className="social-media-title">Audit Details</div>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Created Row */}
          <Box className="audit-details-row" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Box className="view-column-box" sx={{ flex: 1 }}>
              <div className="basic-card-title">Created Date</div>
              <div className="basic-card-value">{formatVacancyDateTime(vacancyData?.createdDate)}</div>
            </Box>
            <Box className="view-column-box" sx={{ flex: 1 }}>
              <div className="basic-card-title">Created By</div>
              <div className="basic-card-value">{vacancyData?.createdByName || 'N/A'}</div>
            </Box>
          </Box>
          {/* Updated Row */}
          <Box className="audit-details-row" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Box className="view-column-box" sx={{ flex: 1 }}>
              <div className="basic-card-title">Updated Date</div>
              <div className="basic-card-value">{formatVacancyDateTime(vacancyData?.updatedDate)}</div>
            </Box>
            <Box className="view-column-box" sx={{ flex: 1 }}>
              <div className="basic-card-title">Updated By</div>
              <div className="basic-card-value">{vacancyData?.updatedByName || 'N/A'}</div>
            </Box>
          </Box>
          {/* Approved Row */}
          <Box className="audit-details-row" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Box className="view-column-box" sx={{ flex: 1 }}>
              <div className="basic-card-title">Approved Date</div>
              <div className="basic-card-value">{formatVacancyDateTime(vacancyData?.approvedDate)}</div>
            </Box>
            <Box className="view-column-box" sx={{ flex: 1 }}>
              <div className="basic-card-title">Approved By</div>
              <div className="basic-card-value">{vacancyData?.vacancyApprovedByName || 'N/A'}</div>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ViewVacancyContent;